// Sending the customer to Paytrail's add-card form, from anywhere a card is needed.
//
// Paytrail's form takes a signed POST. The signature is fresh per request (it carries a timestamp),
// so the parameters are fetched when the customer clicks, then posted straight away - there is no
// need to visit /addcard first. Paytrail returns to <origin><pathPrefix>/addcard/success|error
// (falcon cardstoring.js builds those from `returnurl`); AddCardResult then forwards to whatever
// setAddCardReturn() stored.
import { useState } from 'react'
import { useUiGet } from './useUiGet'

const PAYTRAIL_ADDCARD_FORM = 'https://services.paytrail.com/tokenization/addcard-form'

// At a venue that charges recurring bookings after play from a stored card (appconfig.recurringcardafterplay), a card
// is consented to that only when the customer ticks the box by the add-card button (components/RecurringCardConsentNote)
// - start({ recurringConsent: true }). Bump whenever 'recurring_card_venue_consent_check' changes; falcon keeps the
// list it accepts.
export const RECURRING_VENUE_CONSENT_VERSION = 'recurring-card-1'

// Inside an iframe (an embedded calendar) Paytrail must open in a new tab, like the payment step does.
const isInIframe = () => {
  try {
    return window.self !== window.top
  } catch (e) {
    return true
  }
}

// Builds and submits the form. Exported for the tests' sake as well.
export const submitAddCardForm = (postparameters, doc = document) => {
  const form = doc.createElement('form')
  form.method = 'post'
  form.action = PAYTRAIL_ADDCARD_FORM
  form.target = isInIframe() ? '_blank' : '_self'
  form.style.display = 'none'
  Object.keys(postparameters).forEach((name) => {
    const input = doc.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = postparameters[name]
    form.appendChild(input)
  })
  doc.body.appendChild(form)
  form.submit()
  return form
}

// { start, starting, failed }: start() fetches the signed parameters and leaves for Paytrail.
// `failed` is set when that could not begin (backend unavailable, venue without card storing).
// start({ assignmentid }) adds the card for a continuous membership: the backend puts it on that
// subscription, and the return is /addcard/subscriptionerror when the card was saved but not changed.
export function useAddCard({ appconfig }) {
  const uiGet = useUiGet({ appconfig })
  const [starting, setStarting] = useState(false)
  const [failed, setFailed] = useState(false)

  // options: { assignmentid } for a continuous membership's card, { recurringConsent: true } when the customer ticked
  // the recurring-bookings consent. Anything else (e.g. a click event from onClick={start}) is ignored.
  const start = (options) => {
    if (starting || !uiGet) return
    setStarting(true)
    setFailed(false)
    const request = {
      q: 'getCardTokenizingParamsPc',
      customerid: appconfig.accountkey,
      returnurl: window.location.origin + appconfig.pathPrefix,
    }
    // only a real { assignmentid } - not a click event passed along by onClick={start}
    if (options && typeof options.assignmentid === 'string') {
      request.subscription = { assignmentid: options.assignmentid }
    }
    // only on the customer's explicit choice, never just because the venue charges recurring bookings by card
    if (appconfig.recurringcardafterplay && options && options.recurringConsent === true) {
      request.recurringconsent = { version: RECURRING_VENUE_CONSENT_VERSION }
    }
    uiGet(request)
      .then((response) => {
        const data = response && response.data
        if (!data || data.error || !data.postparameters) {
          setFailed(true)
          setStarting(false)
          return
        }
        submitAddCardForm(data.postparameters)
        // In an iframe the form opened a new tab and this page stays; let the button work again.
        if (isInIframe()) setStarting(false)
      })
      .catch(() => {
        setFailed(true)
        setStarting(false)
      })
  }

  return { start, starting, failed }
}
